import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { Button, Card, Row, Col, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import Select from 'react-select';
import PageHeader from 'components/common/PageHeader';
import FullPageSpinner from 'components/common/full-page-spinner';
import {
  getDepartments,
  getFinancialYears,
  getDepartmentSpendingSummaryFullYear
} from 'services/department-spending/departmentSpendingService';
import DepartmentSpendingBudgetChart from './DepartmentSpendingBudgetChart';
import DepartmentSpendingCurrentMonthGauge from './DepartmentSpendingCurrentMonthGauge';
import DepartmentSpendingFixedSpendingModal from './DepartmentSpendingFixedSpendingModal';
import DepartmentSpendingMonthDetailTable from './DepartmentSpendingMonthDetailTable';
import DepartmentSpendingTransactionsModal from './DepartmentSpendingTransactionsModal';
import { useDepartmentSpendingSelection } from './useDepartmentSpendingSelection';
import { sortSummaryPeriodsChronologically } from './summaryOrdering';

const DepartmentSpendingDashboard = () => {
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [financialYearOptions, setFinancialYearOptions] = useState([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(true);

  const [department, setDepartment] = useState(null);
  const [financialYear, setFinancialYear] = useState(null);

  const [summary, setSummary] = useState(null);
  const [isLoadingSummary, setIsLoadingSummary] = useState(false);

  const [selectedPeriodIndexes, setSelectedPeriodIndexes] = useState([]);
  const [selectedCell, setSelectedCell] = useState(null);

  const [isFixedSpendingModalOpen, setIsFixedSpendingModalOpen] =
    useState(false);
  const [summaryReloadCount, setSummaryReloadCount] = useState(0);
  const [isFixedLinesHighlighted, setIsFixedLinesHighlighted] = useState(false);

  useEffect(() => {
    const loadOptions = async () => {
      setIsLoadingOptions(true);
      try {
        const [departmentsRes, financialYearsRes] = await Promise.all([
          getDepartments(),
          getFinancialYears()
        ]);
        if (departmentsRes?.success === false)
          toast.error(departmentsRes.message);
        if (financialYearsRes?.success === false)
          toast.error(financialYearsRes.message);
        setDepartmentOptions(departmentsRes?.data || []);
        setFinancialYearOptions(financialYearsRes?.data || []);
      } catch (error) {
        console.log('AXIOS ERROR', error);
      } finally {
        setIsLoadingOptions(false);
      }
    };
    loadOptions();
  }, []);

  useLayoutEffect(() => {
    if (!department || !financialYear) {
      setSummary(null);
      return;
    }
    const loadSummary = async () => {
      setIsLoadingSummary(true);
      try {
        const res = await getDepartmentSpendingSummaryFullYear(
          department.value,
          financialYear.value
        );
        if (res?.success === false) toast.error(res.message);
        setSummary(sortSummaryPeriodsChronologically(res?.data) || null);
      } catch (error) {
        console.log('AXIOS ERROR', error);
        setSummary(null);
      } finally {
        setIsLoadingSummary(false);
      }
    };
    loadSummary();
  }, [department, financialYear, summaryReloadCount]);

  const departmentSelectOptions = useMemo(
    () =>
      departmentOptions.map(d => ({
        value: d.code,
        label: d.name
      })),
    [departmentOptions]
  );

  const financialYearSelectOptions = useMemo(
    () =>
      financialYearOptions.map(fy => ({
        value: fy.id,
        label: fy.label
      })),
    [financialYearOptions]
  );

  const { handleDepartmentChange, handleFinancialYearChange } =
    useDepartmentSpendingSelection({
      departmentSelectOptions,
      financialYearSelectOptions,
      department,
      setDepartment,
      financialYear,
      setFinancialYear
    });

  useEffect(() => {
    setSelectedPeriodIndexes([]);
    setSelectedCell(null);
    setIsFixedLinesHighlighted(false);
  }, [department, financialYear]);

  const handleFixedAdjustedGaugeClick = currentPeriodIndex => {
    const isTurningHighlightOn = !isFixedLinesHighlighted;
    setIsFixedLinesHighlighted(isTurningHighlightOn);
    if (
      isTurningHighlightOn &&
      !selectedPeriodIndexes.length &&
      currentPeriodIndex >= 0
    ) {
      setSelectedPeriodIndexes([currentPeriodIndex]);
    }
  };

  const handlePeriodClick = (periodIndex, isMulti) => {
    setSelectedPeriodIndexes(prev => {
      if (isMulti) {
        return prev.includes(periodIndex)
          ? prev.filter(index => index !== periodIndex)
          : [...prev, periodIndex].sort((a, b) => a - b);
      }
      if (prev.length === 1 && prev[0] === periodIndex) return [];
      return [periodIndex];
    });
  };

  const handleFixedSpendingSaved = () => {
    setIsFixedSpendingModalOpen(false);
    setSummaryReloadCount(count => count + 1);
  };

  const hasSelection = !!department && !!financialYear;

  return (
    <>
      <PageHeader
        title="Department Spending Dashboard"
        description="Actual vs budget for the full financial year, and current month spend by department."
        className="mb-3"
      />

      <Card>
        <Card.Header>
          <Row className="g-3">
            <Col xs={12} sm={6} md={4} lg={3}>
              <Form.Group controlId="department-spending-dashboard-department">
                <Form.Label>Department</Form.Label>
                <Select
                  inputId="department-spending-dashboard-department"
                  isClearable
                  isLoading={isLoadingOptions}
                  options={departmentSelectOptions}
                  value={department}
                  onChange={handleDepartmentChange}
                  placeholder="Select department..."
                  classNamePrefix="react-select"
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                />
              </Form.Group>
              {hasSelection && (
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 mt-1"
                  disabled={isLoadingSummary || !summary}
                  onClick={() => setIsFixedSpendingModalOpen(true)}
                >
                  Edit Fixed Spending
                </Button>
              )}
            </Col>

            <Col xs={12} sm={6} md={4} lg={3}>
              <Form.Group controlId="department-spending-dashboard-financial-year">
                <Form.Label>Budget Year To Date</Form.Label>
                <Select
                  inputId="department-spending-dashboard-financial-year"
                  isClearable
                  isLoading={isLoadingOptions}
                  options={financialYearSelectOptions}
                  value={financialYear}
                  onChange={handleFinancialYearChange}
                  placeholder="Select financial year..."
                  classNamePrefix="react-select"
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Header>

        <Card.Body>
          {hasSelection ? (
            <>
              <Row>
                <Col md={8}>
                  <DepartmentSpendingBudgetChart
                    summary={summary}
                    selectedPeriodIndexes={selectedPeriodIndexes}
                    onPeriodClick={handlePeriodClick}
                  />
                </Col>
                <Col md={4}>
                  <DepartmentSpendingCurrentMonthGauge
                    summary={summary}
                    selectedPeriodIndexes={selectedPeriodIndexes}
                    isFixedLinesHighlighted={isFixedLinesHighlighted}
                    onFixedAdjustedGaugeClick={handleFixedAdjustedGaugeClick}
                  />
                </Col>
              </Row>
              {selectedPeriodIndexes.length > 0 && (
                <DepartmentSpendingMonthDetailTable
                  summary={summary}
                  periodIndexes={selectedPeriodIndexes}
                  isFixedLinesHighlighted={isFixedLinesHighlighted}
                  onCellClick={setSelectedCell}
                />
              )}
            </>
          ) : (
            <div className="text-center text-muted p-5">
              Select a department and a financial year to view the dashboard.
            </div>
          )}
        </Card.Body>
      </Card>

      <DepartmentSpendingTransactionsModal
        show={!!selectedCell}
        onClose={() => setSelectedCell(null)}
        department={department?.value}
        financialYearId={financialYear?.value}
        cell={selectedCell}
      />

      <DepartmentSpendingFixedSpendingModal
        show={isFixedSpendingModalOpen}
        onClose={() => setIsFixedSpendingModalOpen(false)}
        onSaved={handleFixedSpendingSaved}
        department={department}
        financialYear={financialYear}
        rows={summary?.rows}
      />

      <FullPageSpinner
        show={isLoadingOptions || isLoadingSummary}
        quantity={3}
        variant="primary"
      />
    </>
  );
};

export default DepartmentSpendingDashboard;
